/**
 * Single source of truth for plan pricing and usage units. Deliberately
 * dependency-free: browser bundles (marketing, frontend, shared-ui) import it
 * via the `@sc/stripe/pricing` deep path so they never touch the barrel
 * (which pulls in the Stripe SDK and the generated Prisma client).
 */

/** One page import = 1000 normalized tokens. */
export const TOKENS_PER_PAGE = 1000;

/** One query = 50,000 normalized tokens at the default 1x multiplier. */
export const TOKENS_PER_QUERY = 50_000;

/** Matches CompanySubscriptionType / SubscriptionType enum values. */
export type SubscriptionTierType = 'enterprise' | 'essentials';

/**
 * Per-query richness multiplier cap. A multiplier of n allows
 * n × TOKENS_PER_QUERY tokens per query, consuming n queries from the
 * monthly allocation.
 */
export const maxQueryMultiplierBySubscription: Record<
  SubscriptionTierType,
  number
> = {
  essentials: 3,
  enterprise: Infinity,
};

export const maxDomainsBySubscription: Record<SubscriptionTierType, number> = {
  essentials: 5,
  enterprise: Infinity,
};

/**
 * Multiplier implied by a stored token budget. Legacy rows can hold
 * sub-50k values from the old credit scheme; they read as 1x.
 */
export const queryMultiplierForTokens = (tokensPerQuery: number): number =>
  Math.max(1, Math.round(tokensPerQuery / TOKENS_PER_QUERY));

export interface EssentialsStepInterface {
  /** Queries per month at the default 1x multiplier. */
  queries: number;
  /** Page imports per month. */
  pages: number;
  /**
   * Monthly price in pounds (may be fractional). Display only — the charge
   * comes from the per-step Stripe price resolved by lookup key.
   */
  priceGbp: number;
}

export const ESSENTIALS_STEPS: readonly EssentialsStepInterface[] = [
  { queries: 500, pages: 5_000, priceGbp: 75 },
  { queries: 750, pages: 7_500, priceGbp: 112.5 },
  { queries: 1_000, pages: 10_000, priceGbp: 150 },
  { queries: 1_250, pages: 12_500, priceGbp: 187.5 },
  { queries: 1_500, pages: 15_000, priceGbp: 225 },
  { queries: 1_750, pages: 17_500, priceGbp: 262.5 },
  { queries: 2_000, pages: 20_000, priceGbp: 300 },
  { queries: 3_000, pages: 25_000, priceGbp: 437.5 },
  { queries: 4_000, pages: 30_000, priceGbp: 575 },
  { queries: 5_000, pages: 35_000, priceGbp: 712.5 },
  { queries: 10_000, pages: 70_000, priceGbp: 1_425 },
];

export const SCALE_MIN_QUERIES = 10_000;

/** The Stripe lookup key of a step's price, e.g. "essentials_500_1000". */
export const essentialsPriceLookupKey = (
  step: EssentialsStepInterface
): string => `essentials_${step.queries}_${step.pages}`;

/** "£112.50" for fractional prices, "£75" for whole-pound ones. */
export const formatPriceGbp = (value: number): string =>
  `£${value.toLocaleString('en-GB', {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;

/**
 * The free-trial grant minted at signup, in queries/pages. Fractional queries
 * are fine — the grant is stored in tokens; display floors to whole units.
 */
export const TRIAL_ALLOWANCE = { queries: 30, pages: 2000 }; // 30 = 1.5m tokens, 2000 = 2m tokens = $1.75 per trial

/**
 * The mid-cycle upgrade credit: the unused portion of the current plan's
 * allowance, valued at that plan's own rates, applied as a discount on the
 * new plan's first invoice. Usage-based, not time-based — only consumption
 * counts. The plan cost is split between queries and pages by their token
 * share (the denominator MUST be the combined token total, or a page-heavy
 * plan would drive the query allocation negative), then each unit of usage
 * consumes its share.
 *
 * Bounded [0, costGbp]: full usage gives £0, and the result never goes
 * negative. All inputs are in tokens so partial units are valued exactly.
 */
export const calculateUpgradeDiscountGbp = ({
  costGbp,
  queryTokens,
  pageTokens,
  usedQueryTokens,
  usedPageTokens,
}: {
  /** The current plan's monthly price in pounds. */
  costGbp: number;
  /** The current plan's query allowance, in tokens. */
  queryTokens: number;
  /** The current plan's page-import allowance, in tokens. */
  pageTokens: number;
  /** Query tokens consumed so far this cycle. */
  usedQueryTokens: number;
  /** Page-import tokens consumed so far this cycle. */
  usedPageTokens: number;
}): number => {
  const totalTokens = queryTokens + pageTokens;

  if (costGbp <= 0 || totalTokens <= 0) return 0;

  const pageAlloc = costGbp * (pageTokens / totalTokens);
  const queryAlloc = costGbp - pageAlloc;

  const usedQueryValue =
    queryTokens > 0 ? queryAlloc * (usedQueryTokens / queryTokens) : 0;

  const usedPageValue =
    pageTokens > 0 ? pageAlloc * (usedPageTokens / pageTokens) : 0;

  return Math.min(
    Math.max(costGbp - usedQueryValue - usedPageValue, 0),
    costGbp
  );
};

/** The backend `enterprise` tier is sold and displayed as "Scale". */
export const planDisplayName = (
  type: SubscriptionTierType | null | undefined
): string => {
  if (type === 'enterprise') return 'Scale';
  if (type === 'essentials') return 'Essentials';
  return 'Start';
};

/**
 * Maps a stored subscription subtotal (whole pounds, ex-VAT) back to its
 * slider step; null for legacy amounts (e.g. the pre-slider £99 plan).
 */
export const stepIndexForSubtotal = (subtotal: number): number | null => {
  const index = ESSENTIALS_STEPS.findIndex(
    (step) => step.priceGbp === subtotal
  );

  return index === -1 ? null : index;
};
